import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import DateRange from "components/dateRangePicker/DateRange";
import MDButton from "components/MDButton";
import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getBlogCategories, getBlogAuthors, migrateAuthor } from "../../../redux/apis/blogs";
import { formatTimestampToMapOld } from "utilities/common";
import { statesData } from "utilities/common/Cities";

const selectAll = {
    m: 0,
    mb: 0.5,
    padding: '4.5px 8px',
    borderRadius: '0.375rem',
    transition: 'background-color 300ms ease, color 300ms ease',
    ':hover': {
        backgroundColor: '#f0f2f5'
    }
};

/**
 * Checks if an object has any non-empty values, including nested objects
 * @param {Object} obj - The object to check
 * @returns {boolean} - True if any non-empty value exists, false otherwise
 */
function hasAnyValue(obj) {
    if (!obj || typeof obj !== 'object') return false;

    // Special handling for dateRange
    if (obj.dateRange) {
        const dateRange = Array.isArray(obj.dateRange) ? obj.dateRange : [];
        // Check if dateRange has at least one valid date
        const hasValidDates = dateRange.some(date =>
            date instanceof Date && !isNaN(date)
        );

        if (hasValidDates) return true;
    }

    // Check other properties
    return Object.keys(obj).some(key => {
        if (key === 'dateRange') return false; // Already checked

        const value = obj[key];
        if (value && typeof value === 'object') {
            return hasAnyValue(value); // Recursively check nested objects
        }
        return value !== '' && value !== null && value !== undefined;
    });
}

export default function FilterBlogs(props = {}) {
    const dispatch = useDispatch();
    const { applyBlogFilter, filterMode = '', getQueryValues = {}, setIsLoading = () => { }, blogFilterPayload = {} } = props;
    const states = statesData
    const [formData, setFormData] = useState({
        categories: [],
        authors: [],
        status: '',
        state: [],
        dateRange: [null, null]
    });

    const { categories = [], migrateAuthorsList: authorsData = [], } = useSelector(state => state.blogs);
    const authors = authorsData?.data
    console.log("authorsssss", authors);
    useEffect(() => {
        // Fetch categories and authors when component mounts
        dispatch(getBlogCategories());
        dispatch(migrateAuthor({ skip: 0, limit: 100 })).unwrap().then(res => {
        }).catch(err => {
            console.log(err)
        })
    }, [dispatch]);

    const isAllCategoriesSelected = useMemo(() => {
        return categories?.length > 0 && formData?.categories.length === categories?.length;
    }, [formData?.categories, categories]);

    const isAllAuthorsSelected = useMemo(() => {
        return authors?.length > 0 && formData?.authors.length === authors?.length;
    }, [formData?.authors, authors]);

    const isAllStatesSelected = useMemo(() => {
        return states?.length > 0 && formData?.state.length === states?.length;
    }, [formData?.state, states]);

    const clearAllHandler = () => {
        setFormData({
            categories: [],
            authors: [],
            status: '',
            state: [],
            dateRange: [null, null]
        });
        applyBlogFilter({});
    };

    const showApplyButton = useMemo(() => {
        return hasAnyValue(formData);
    }, [formData]);

    const applyFiltersHandler = () => {
        const payload = {};
        if (formData.categories.length > 0) payload.category = formData.categories;
        if (formData.authors.length > 0) payload.author = formData.authors;
        if (formData.status !== 'all') payload.status = formData.status;
        if (formData.state.length > 0) payload.state = formData.state;
        if (formData.dateRange[0]) payload.startDate = formatTimestampToMapOld(formData.dateRange[0]);
        if (formData.dateRange[1]) payload.endDate = formatTimestampToMapOld(formData.dateRange[1]);
        applyBlogFilter(payload);
    }



    const setDateRange = (dates) => {
        setFormData(prev => ({ ...prev, dateRange: dates }));
    }

    return (
        <Box className='cus-filter-sec' mb={2}>
            <Grid className='filter-box' container alignItems='center' justifyContent='flex-end' spacing={1.5}>
                <Grid item xs={12} sm={4} md={4} xxl={2}>
                    <Box className='filter-price-unit'>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                className="form-select"
                                size='small'
                                multiple
                                value={formData.categories}
                                label='Category'
                                onChange={(e) => setFormData(prev => ({ ...prev, categories: e.target.value }))}
                                renderValue={(selected) => isAllCategoriesSelected ? 'All' : selected.join(', ')}
                            >
                                <FormControlLabel
                                    sx={{ ...selectAll, backgroundColor: isAllCategoriesSelected ? '#f0f2f5' : '' }}
                                    control={
                                        <Checkbox
                                            checked={isAllCategoriesSelected}
                                            indeterminate={formData.categories.length > 0 && !isAllCategoriesSelected}
                                            onChange={(e) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    categories: e.target.checked ? categories.map(c => c.slug) : []
                                                }));
                                            }}
                                        />
                                    }
                                    label='Select All'
                                />
                                {categories.map(category => (
                                    <MenuItem key={category.slug} value={category.slug}>
                                        <Checkbox checked={formData.categories.includes(category.slug)} />
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} md={4} xxl={2}>
                    <Box className='filter-price-unit'>
                        <FormControl fullWidth>
                            <InputLabel>Author</InputLabel>
                            <Select
                                className="form-select"
                                size='small'
                                multiple
                                value={formData.authors}
                                label='Author'
                                onChange={(e) => setFormData(prev => ({ ...prev, authors: e.target.value }))}
                                renderValue={(selected) => isAllAuthorsSelected ? 'All' : selected.join(', ')}
                            >
                                <FormControlLabel
                                    sx={{ ...selectAll, backgroundColor: isAllAuthorsSelected ? '#f0f2f5' : '' }}
                                    control={
                                        <Checkbox
                                            checked={isAllAuthorsSelected}
                                            indeterminate={formData.authors.length > 0 && !isAllAuthorsSelected}
                                            onChange={(e) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    authors: e.target.checked ? authors?.map(a => a.name) : []
                                                }));
                                            }}
                                        />
                                    }
                                    label='Select All'
                                />
                                {authors?.map(author => (
                                    <MenuItem key={author?.name} value={author.name}>
                                        <Checkbox checked={formData.authors.includes(author.name)} />
                                        {author?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} md={4} xxl={2}>
                    <Box className='filter-price-unit'>
                        <FormControl fullWidth>
                            <InputLabel>State</InputLabel>
                            <Select
                                className="form-select"
                                size='small'
                                multiple
                                value={formData.state}
                                label='State'
                                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                                renderValue={(selected) => isAllStatesSelected ? 'All' : selected.join(', ')}
                            >
                                <FormControlLabel
                                    sx={{ ...selectAll, backgroundColor: isAllStatesSelected ? '#f0f2f5' : '' }}
                                    control={
                                        <Checkbox
                                            checked={isAllStatesSelected}
                                            indeterminate={formData.state.length > 0 && !isAllStatesSelected}
                                            onChange={(e) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    state: e.target.checked ? states?.map(a => a.label) : []
                                                }));
                                            }}
                                        />
                                    }
                                    label='Select All'
                                />
                                {states?.map(data => (
                                    <MenuItem key={data?.label} value={data.value}>
                                        <Checkbox checked={formData.state.includes(data.label)} />
                                        {data?.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} md={4} xxl={2}>
                    <Box className="filter-date-range">
                        <FormControl fullWidth>
                            <DateRange
                                setDateRange={setDateRange}
                                dateRange={formData.dateRange}
                                showIcon
                                name='range'
                                title='Select Date'
                            />
                        </FormControl>
                    </Box>
                </Grid>
                {/* <Grid item xs={12} sm={4} md={4} xxl={2}>
                    <Box className='filter-pro-status'>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                size='small'
                                value={formData.status}
                                label='Status'
                                onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
                            >
                                <MenuItem value='all'>All</MenuItem>
                                <MenuItem value='active'>Active</MenuItem>
                                <MenuItem value='inactive'>Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Grid> */}
                <Grid item xs={12} sm={4} md={4} xxl={1.5}>
                    <Box className='filter-btns'>
                        <Stack direction='row' alignItems='center' gap={1} className='w-100'>
                            <Button
                                className='filter-clear-all'
                                onClick={clearAllHandler}
                                size='small'
                                variant='text'
                                color='secondary'
                                style={{ width: '100%' }}
                                disabled={!showApplyButton}
                            >
                                Clear All
                            </Button>
                            <MDButton
                                disabled={!showApplyButton}
                                className='filter-apply'
                                onClick={applyFiltersHandler}
                                size='small'
                                variant='contained'
                                color='primary'
                                style={{ width: '100%' }}
                            >
                                Apply
                            </MDButton>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}